import { SectionLabel, BulletList, CTASection } from "@/components/Section";
import Reveal from "@/components/Reveal";
import {
  CheckCircle2,
  XCircle,
  Building2,
  Wrench,
  HardHat,
  Sprout,
  Zap,
  Hammer,
  Trees,
  Cog,
  Home as HomeIcon,
  Store,
  ArrowRight,
} from "lucide-react";

const HERO_PHOTO =
  "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/ewf5a3io_Messenger_creation_F2758CFA-025E-4A80-82BF-8013FE90AE4E.jpeg";

const industries = [
  { icon: Zap, label: "Electrical Contractors" },
  { icon: Hammer, label: "Civil & Construction" },
  { icon: Trees, label: "Landscaping" },
  { icon: Cog, label: "Mechanical Services" },
  { icon: HomeIcon, label: "Property Services" },
  { icon: Store, label: "Commercial Services" },
];

const beforeAfter = [
  {
    before: "Invoices delayed",
    after: "Weekly billing rhythm",
  },
  {
    before: "Margins unclear",
    after: "Live margin visibility",
  },
  {
    before: "Owner overloaded",
    after: "Decisions delegated",
  },
  {
    before: "Reactive firefighting",
    after: "Structured weekly ops",
  },
  {
    before: "No reporting cadence",
    after: "Numbers every Monday",
  },
];

const ideal = [
  { icon: Wrench, label: "Trade businesses" },
  { icon: Building2, label: "Service businesses" },
  { icon: HardHat, label: "Contractor-led companies" },
  { icon: Sprout, label: "Growing SMEs" },
];

const profile = [
  "$2M–$15M+ revenue",
  "Owner-led",
  "Growing but stretched",
  "Systems under pressure",
  "No full-time COO",
];

const notSuitable = [
  "Startups",
  "Solo operators",
  "Lifestyle businesses",
  "Businesses under $500k revenue",
];

export default function WhoItsFor() {
  return (
    <div data-testid="who-its-for-page">
      {/* HERO with photo */}
      <section
        className="relative border-b border-[#E2E8F0] bg-white overflow-hidden"
        data-testid="who-its-for-hero"
      >
        <div className="bb-grid-bg absolute inset-0 opacity-60 pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 pt-10 lg:pt-16 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <SectionLabel>Who It's For</SectionLabel>
            <h1
              className="bb-fade-up mt-5 text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]"
              data-testid="who-its-for-title"
            >
              Built for owner-led service businesses.
            </h1>
            <p className="bb-fade-up bb-delay-2 mt-8 text-lg lg:text-xl text-[#475569] leading-relaxed max-w-2xl">
              $2M–$15M turnover. Outgrown the systems that got you there.
              If that's you, we'll fit in fast.
            </p>
          </div>

          <div className="bb-fade-up bb-delay-3 lg:col-span-5">
            <div className="relative aspect-[4/5] sm:aspect-[5/5] overflow-hidden">
              <img
                src={HERO_PHOTO}
                alt="NZ contractor on-site"
                className="w-full h-full object-cover"
                data-testid="who-its-for-hero-photo"
              />
              {/* Corner ticks */}
              <span className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#F68712]" />
              <span className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#F68712]" />
              <span className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#F68712]" />
              <span className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#F68712]" />

              {/* Caption strip */}
              <div className="absolute bottom-0 inset-x-0 bg-[#0A192F]/90 backdrop-blur-sm px-5 py-3 flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                  OWNER-LED · NZ
                </span>
                <span className="text-xs font-bold text-white tracking-wide">
                  $2M – $15M+ REVENUE
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ideal Clients */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="ideal-clients-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <SectionLabel>01 — Ideal Clients</SectionLabel>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F] max-w-3xl">
            Operationally heavy. Systems under pressure.
          </h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#E2E8F0]">
            {ideal.map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.label} delay={i * 80}>
                  <div
                    className="bb-card p-8 lg:p-10 border-r border-b border-[#E2E8F0] flex flex-col gap-6 min-h-[200px] h-full"
                    data-testid={`ideal-client-${i}`}
                  >
                    <Icon size={28} strokeWidth={1.5} className="text-[#0A192F]" />
                    <h3 className="text-xl font-bold tracking-tight text-[#0A192F] mt-auto">
                      {c.label}
                    </h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE WORK WITH */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="industries-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <Reveal>
            <SectionLabel>Industries</SectionLabel>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <h2 className="lg:col-span-7 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
                Who we work with.
              </h2>
              <p className="lg:col-span-5 text-base lg:text-lg text-[#475569] leading-relaxed">
                Trade and service businesses across Aotearoa — anywhere operations are heavy, margin is tight, and the owner is stretched.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 lg:mt-16 grid grid-cols-2 lg:grid-cols-6 border-t border-l border-[#E2E8F0]">
            {industries.map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.label} delay={i * 80}>
                  <div
                    className="bb-card p-6 lg:p-7 border-r border-b border-[#E2E8F0] flex flex-col items-start gap-4 min-h-[160px] h-full"
                    data-testid={`industry-${i}`}
                  >
                    <Icon size={26} strokeWidth={1.5} className="text-[#F68712]" />
                    <h3 className="text-sm lg:text-base font-extrabold tracking-tight text-[#0A192F] leading-snug mt-auto">
                      {ind.label}
                    </h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* BEFORE / AFTER */}
      <section className="bg-[#0A192F] text-white border-b border-[#E2E8F0]" data-testid="before-after-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <Reveal>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
              Before / After
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] max-w-3xl">
              What changes on the ground.
            </h2>
          </Reveal>

          <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-4 lg:gap-8 items-stretch">
            {/* Before column */}
            <Reveal direction="right">
              <div className="border border-white/15 p-6 lg:p-8 h-full" data-testid="before-column">
                <div className="font-mono text-[11px] tracking-widest text-white/50 font-bold mb-6">
                  BEFORE · CHAOS
                </div>
                <ul className="space-y-4">
                  {beforeAfter.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-base lg:text-lg font-semibold text-white/80 line-through decoration-[#0A192F]/0"
                    >
                      <XCircle size={18} strokeWidth={1.75} className="text-white/40 mt-1 flex-shrink-0" />
                      <span>{b.before}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* Arrow divider */}
            <div className="hidden lg:flex items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <div className="h-16 w-px bg-[#F68712]" />
                <ArrowRight size={28} strokeWidth={2} className="text-[#F68712]" />
                <div className="h-16 w-px bg-[#F68712]" />
              </div>
            </div>

            {/* After column */}
            <Reveal direction="left" delay={120}>
              <div className="bg-[#F68712] text-[#0A192F] p-6 lg:p-8 h-full" data-testid="after-column">
                <div className="font-mono text-[11px] tracking-widest text-[#0A192F]/70 font-bold mb-6">
                  AFTER · STRUCTURE
                </div>
                <ul className="space-y-4">
                  {beforeAfter.map((b, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-base lg:text-lg font-bold text-[#0A192F]"
                    >
                      <CheckCircle2 size={18} strokeWidth={2} className="text-[#0A192F] mt-1 flex-shrink-0" />
                      <span>{b.after}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Typical Profile */}
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]" data-testid="typical-profile-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionLabel color="navy">02 — Typical Profile</SectionLabel>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
              You'll recognise yourself here.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-[#0A192F]/15">
            <BulletList items={profile} testId="typical-profile-list" />
          </div>
        </div>
      </section>

      {/* Not Suitable */}
      <section className="bg-[#0A192F] text-white" data-testid="not-suitable-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
              03 — Not Suitable For
            </div>
            <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
              We're not for everyone.
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed max-w-md">
              Backbench is built for operational businesses with structural pressure.
              If that's not you yet — we're probably not the right fit.
            </p>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/15">
            <ul className="space-y-4" data-testid="not-suitable-list">
              {notSuitable.map((n, i) => (
                <li
                  key={n}
                  className="flex items-start gap-4 text-lg lg:text-xl font-bold"
                  data-testid={`not-suitable-item-${i}`}
                >
                  <XCircle size={22} strokeWidth={1.75} className="text-[#F68712] mt-0.5 flex-shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
            <div className="mt-12 pt-8 border-t border-white/10 flex items-start gap-3 text-sm text-white/60 max-w-lg">
              <CheckCircle2 size={16} className="text-[#F68712] mt-0.5 flex-shrink-0" />
              <p>
                If you're between $1M–$2M revenue and scaling fast, talk to us anyway —
                we'll tell you straight if we can help.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
